import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  Compass,
  FileText,
  Globe2,
  Headphones,
  ShieldCheck,
  TrendingUp,
  Wrench,
  Zap,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import {
  DIFFERENTIATORS,
  DELIVERIES,
  PRODUCT_LINES,
  PROOF,
  SEGMENTS,
  WHATSAPP_URL,
} from "@/lib/site";
import QuoteForm from "@/components/site/QuoteForm";

const ICONS = { Headphones, Zap, ShieldCheck, Wrench, Compass, TrendingUp };
const FEATURED_LINES = PRODUCT_LINES.filter((p) =>
  ["consumiveis", "spare-parts"].includes(p.slug)
);

export default function Home() {
  return (
    <>
      <section
        data-testid="home-hero"
        className="relative overflow-hidden bg-white"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-bronze/40 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-surface-line bg-surface-alt px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-bronze shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                Fornecimento industrial especializado
              </div>

              <h1 className="mt-7 max-w-4xl font-display text-5xl font-black leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-7xl">
                Equipamentos e suprimentos para operações críticas.
              </h1>

              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ink-muted">
                A Wellpec fornece equipamentos de campo, spare parts e materiais
                industriais com foco em rastreabilidade, agilidade comercial e
                suporte técnico para setores de alta exigência.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contato"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 font-semibold text-white shadow-lg shadow-brand/15 transition-all hover:-translate-y-0.5 hover:bg-brand-mid hover:shadow-xl"
                >
                  Solicitar orçamento
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/produtos"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-surface-line bg-white px-7 py-4 font-semibold text-brand shadow-sm transition-all hover:-translate-y-0.5 hover:border-bronze/50 hover:bg-surface-alt"
                >
                  Ver catálogo
                  <BookOpen className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { icon: FileText, title: "Rastreabilidade", desc: "Documentação e origem técnica." },
                  { icon: Globe2, title: "Brasil + LATAM", desc: "Atendimento regional." },
                  { icon: ShieldCheck, title: "Operação crítica", desc: "Itens que não podem falhar." },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="rounded-lg border border-surface-line bg-white p-5 shadow-sm"
                    >
                      <Icon className="h-5 w-5 text-brand" />
                      <div className="mt-4 font-display font-bold text-ink">
                        {item.title}
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-ink-muted">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -left-6 -top-6 hidden h-40 w-40 border-l border-t border-bronze/40 lg:block" />
                <div className="absolute -bottom-6 -right-6 hidden h-40 w-40 border-b border-r border-brand/25 lg:block" />
                <div className="relative overflow-hidden rounded-lg border border-surface-line bg-surface-alt shadow-2xl shadow-ink/10">
                  <img
                    src="/images/brand/unidades-bombeio-petroleo.png"
                    alt="Unidades de bombeio de petróleo em operação"
                    loading="eager"
                    className="h-[520px] w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand/25 via-transparent to-transparent" />
                </div>
                <div className="mt-4 rounded-lg border border-surface-line bg-white p-5 shadow-sm">
                  <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
                    Capacidade técnica
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {PROOF.slice(0, 3).map((p) => (
                      <div key={p.label} className="border-l border-surface-line pl-4 first:border-l-0 first:pl-0">
                        <div className="font-display text-2xl font-black text-brand">
                          {p.value}
                        </div>
                        <div className="mt-1 text-xs leading-snug text-ink-muted">
                          {p.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="home-about" className="bg-surface-alt py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Quem somos"
              title="Uma parceira técnica para compras industriais de alta responsabilidade."
              subtitle="Combinamos curadoria de fornecedores, leitura técnica da demanda e processo comercial enxuto para reduzir atrito em cotações, reposições e fornecimentos recorrentes."
              cta={{ to: "/sobre", label: "Conheça a Wellpec" }}
            />
          </div>
          <div className="lg:col-span-7 grid gap-4 sm:grid-cols-2">
            {[
              {
                t: "Especificação correta",
                d: "Apoio para entender aplicação, compatibilidade, documentação e criticidade do item.",
              },
              {
                t: "Fornecimento integrado",
                d: "Equipamentos, spare parts e materiais industriais em um único fluxo comercial.",
              },
              {
                t: "Agilidade de cotação",
                d: "Comunicação objetiva para transformar demanda técnica em proposta comercial.",
              },
              {
                t: "Rastreabilidade",
                d: "Foco em procedência, documentação e segurança para operações críticas.",
              },
            ].map((b) => (
              <div
                key={b.t}
                className="rounded-lg border border-surface-line bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-bronze/40 hover:shadow-lg"
              >
                <CheckCircle2 className="h-5 w-5 text-brand" />
                <h4 className="mt-4 font-display font-bold text-ink">{b.t}</h4>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {b.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="home-products"
        className="border-y border-surface-line bg-white py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <SectionHeading
                eyebrow="Catálogo"
                title="Linhas essenciais para manter sua operação em movimento."
                subtitle="Acesse o catálogo técnico e solicite uma cotação com base em aplicação, quantidade, prazo e documentação requerida."
              />
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/produtos"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-mid hover:shadow-lg"
              >
                Abrir catálogo <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {FEATURED_LINES.map((p) => (
              <Link
                key={p.slug}
                to="/produtos"
                data-testid={`product-card-${p.slug}`}
                className="group overflow-hidden rounded-lg border border-surface-line bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-bronze/40 hover:shadow-xl"
              >
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 h-64 overflow-hidden bg-surface-alt md:h-auto">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="md:col-span-3 p-7">
                    <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
                      Linha
                    </div>
                    <h3 className="mt-3 font-display text-2xl font-bold text-ink">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                      {p.short}
                    </p>
                    <ul className="mt-5 space-y-3">
                      {p.bullets.slice(0, 3).map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm text-ink">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-bronze shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand group-hover:text-brand-mid">
                      Ver no catálogo <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-surface-line bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading eyebrow="Capacidade comprovada" title="Do escopo técnico à entrega." subtitle="Registros reais de equipamentos e componentes fornecidos para demandas industriais, com atenção à aplicação, especificação e documentação requerida." />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {DELIVERIES.map((item) => (
              <article key={item.title} className="group overflow-hidden rounded-lg border border-surface-line bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                <div className="h-64 overflow-hidden bg-surface-alt"><img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-6">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-bronze">{item.tag}</div>
                  <h3 className="mt-3 font-display text-xl font-bold text-ink">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-muted">{item.desc}</p>
                  <Link to="/contato" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand">Solicitar solução semelhante <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface-alt py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5 overflow-hidden rounded-lg border border-surface-line bg-white p-5 shadow-sm"><img src="/images/brand/wellpec-produtos-feira.jpeg" alt="Produtos técnicos apresentados pela Wellpec" loading="lazy" className="h-80 w-full rounded-md object-cover" /></div>
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">Qualidade e documentação</div>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">Rastreabilidade compatível com a criticidade da sua operação.</h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-muted">Trabalhamos com fabricantes qualificados e disponibilizamos, conforme o produto e o escopo contratado, certificados, relatórios e documentação técnica aplicável.</p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">{["Identificação de fabricante e modelo", "Documentação técnica aplicável", "Acompanhamento do fornecimento", "Validação da especificação"].map((item) => <div key={item} className="flex items-center gap-3 rounded-md border border-surface-line bg-white p-4 text-sm font-medium text-ink"><CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />{item}</div>)}</div>
          </div>
        </div>
      </section>

      <section data-testid="home-diff" className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Diferenciais"
            title="Uma experiência de compra mais técnica, rápida e confiável."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DIFFERENTIATORS.map((d) => {
              const Icon = ICONS[d.icon];
              return (
                <div
                  key={d.title}
                  data-testid={`diff-${d.title}`}
                  className="group rounded-lg border border-surface-line bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:border-bronze/40 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-surface-alt text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                    {Icon && <Icon className="h-5 w-5" />}
                  </div>
                  <h4 className="mt-5 font-display font-bold text-ink">
                    {d.title}
                  </h4>
                  <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                    {d.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        data-testid="home-commercial"
        className="bg-brand-noise py-20 text-white lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze-soft">
              Parceria estratégica
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              Suprimento industrial com clareza técnica e resposta comercial.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/80">
              Reduza paradas não programadas, ganhe previsibilidade de
              fornecimento e centralize suas compras com um parceiro que entende
              a criticidade da sua operação.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contato"
                data-testid="commercial-cta-primary"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-brand transition-all hover:-translate-y-0.5 hover:bg-surface-alt"
              >
                Falar com um especialista <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={WHATSAPP_URL("Olá! Quero conversar com um especialista da Wellpec.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="commercial-cta-wa"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-7 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/15"
              >
                WhatsApp direto
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {PROOF.map((p) => (
              <div
                key={p.label}
                className="rounded-lg border border-white/15 bg-white/5 p-6 backdrop-blur-sm"
              >
                <div className="font-display text-3xl font-black text-white">
                  {p.value}
                </div>
                <div className="mt-2 text-xs leading-snug text-white/70">
                  {p.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-segments" className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Segmentos atendidos"
            title="Operações industriais que confiam na Wellpec."
            subtitle="Nossa experiência atravessa setores em que precisão, confiabilidade e prazo são inegociáveis."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SEGMENTS.map((s, i) => (
              <div
                key={s.title}
                data-testid={`segment-${i}`}
                className="rounded-lg border border-surface-line bg-surface-alt/60 p-7 shadow-sm transition-all hover:-translate-y-1 hover:border-bronze/40 hover:bg-white hover:shadow-lg"
              >
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
                  Setor
                </div>
                <h4 className="mt-3 font-display text-lg font-bold text-ink">
                  {s.title}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="home-cta"
        className="border-t border-surface-line bg-surface-alt py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Orçamento rápido"
              title="Pronto para uma cotação?"
              subtitle="Envie sua demanda e receba retorno com proposta comercial e alternativas técnicas."
            />
            <ul className="mt-8 space-y-4">
              {[
                "Retorno comercial objetivo",
                "Especificação técnica validada",
                "Fornecimento recorrente ou pontual",
                "Atendimento no Brasil e LATAM",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3 text-ink">
                  <CheckCircle2 className="h-5 w-5 text-brand mt-0.5" />
                  <span className="text-sm">{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm source="home" />
          </div>
        </div>
      </section>
    </>
  );
}
